import { defineStore } from 'pinia'
import * as Sentry from '@sentry/nuxt'
import { invoke } from '~/lib/tauriInvoke'
import type { GroupUser } from '~/types/groups'
import { useGroupsStore } from '~/stores/groups'

// The members panel of the open channel, as the server lays it out (Groups.GetChannelMemberList):
// only members who can view the channel, split into sections — one per hoisted role, then the
// rest online, then offline (left out in large groups) — and sorted by name. The client keeps the
// section counts and the parts of the flat list it has fetched, block by block, as the panel
// scrolls; it never sorts or filters itself, so the same contract keeps working for groups far too
// large to load whole.

export interface MemberListSection {
  /** A hoisted role's id, `online` or `offline`. */
  key: string
  role_id: string | null
  count: number
}

export interface MemberListItem {
  user: GroupUser
  section_key: string
}

interface ChannelMemberListResponse {
  sections: MemberListSection[]
  items: MemberListItem[]
  total: number
  offline_hidden: boolean
}

interface ChannelList {
  groupId: string
  sections: MemberListSection[]
  total: number
  offlineHidden: boolean
  /** Sparse: index in the flat list → item; holes are not loaded yet. */
  items: (MemberListItem | undefined)[]
  /** Block indexes fetched since the last invalidation. */
  loadedBlocks: number[]
  /** The first block has answered at least once — the counts are real. */
  ready: boolean
  /** The range the panel last asked for, so an invalidation can re-fetch what is on screen. */
  visibleFrom: number
  visibleTo: number
}

export const MEMBER_LIST_BLOCK = 100

// Bumped by every invalidation of a channel, so a block that answers for the list as it was
// before is dropped.
const versions = new Map<string, number>()
const inFlight = new Set<string>()
const refreshTimers = new Map<string, ReturnType<typeof setTimeout>>()

// After an event the whole group's clients refresh at once; spreading them out keeps that from
// turning into a burst of identical requests.
const REFRESH_DEBOUNCE_MS = 500
const REFRESH_JITTER_MS = 1500

export const useChannelMemberListStore = defineStore('channelMemberList', {
  state: () => ({
    lists: {} as Record<string, ChannelList>,
  }),

  actions: {
    /** Makes sure the blocks covering `[from, to)` are loaded (the panel's visible range). */
    async ensureRange(groupId: string, channelId: string, from: number, to: number) {
      let list = this.lists[channelId]
      if (!list || list.groupId !== groupId) {
        list = this.lists[channelId] = {
          groupId,
          sections: [],
          total: 0,
          offlineHidden: false,
          items: [],
          loadedBlocks: [],
          ready: false,
          visibleFrom: 0,
          visibleTo: MEMBER_LIST_BLOCK,
        }
      }
      list.visibleFrom = Math.max(0, from)
      list.visibleTo = Math.max(list.visibleFrom + 1, to)

      const first = Math.floor(list.visibleFrom / MEMBER_LIST_BLOCK)
      // Before the first answer the length isn't known — just the first block.
      const last = list.ready
        ? Math.min(Math.floor((list.visibleTo - 1) / MEMBER_LIST_BLOCK), Math.max(0, Math.ceil(list.total / MEMBER_LIST_BLOCK) - 1))
        : first
      const tasks: Promise<void>[] = []
      for (let block = first; block <= last; block++) {
        if (!list.loadedBlocks.includes(block)) tasks.push(this.fetchBlock(groupId, channelId, block))
      }
      await Promise.all(tasks)
    },

    async fetchBlock(groupId: string, channelId: string, block: number) {
      const key = `${channelId}:${block}`
      if (inFlight.has(key)) return
      const version = versions.get(channelId) ?? 0
      inFlight.add(key)
      try {
        const res = await invoke<ChannelMemberListResponse>('get_channel_member_list', {
          groupId,
          channelId,
          offset: block * MEMBER_LIST_BLOCK,
          limit: MEMBER_LIST_BLOCK,
        })
        const list = this.lists[channelId]
        if (!list || list.groupId !== groupId || (versions.get(channelId) ?? 0) !== version) return

        list.sections = res.sections
        list.total = res.total
        list.offlineHidden = res.offline_hidden
        list.ready = true
        // The list may have shrunk since the last time it was read.
        if (list.items.length > res.total) list.items.length = res.total
        res.items.forEach((item, i) => {
          list.items[block * MEMBER_LIST_BLOCK + i] = item
        })
        if (!list.loadedBlocks.includes(block)) list.loadedBlocks.push(block)

        useGroupsStore().mergeGroupMembers(groupId, res.items.map((item) => item.user))
      } catch (e) {
        console.error('[ChannelMemberList] Failed to load members', e)
        Sentry.captureException(e, { extra: { action: 'get_channel_member_list', groupId, channelId, block } })
      } finally {
        inFlight.delete(key)
      }
    },

    /**
     * The lists of a group (or one channel) are out of date — a role, a permission or the member set
     * changed. What's on screen stays until the refetch replaces it; the refetch is debounced and
     * jittered so a whole group's clients don't all ask at the same instant.
     */
    invalidate(groupId: string, channelId?: string) {
      for (const [id, list] of Object.entries(this.lists)) {
        if (list.groupId !== groupId || (channelId && id !== channelId)) continue
        versions.set(id, (versions.get(id) ?? 0) + 1)
        list.loadedBlocks = []

        clearTimeout(refreshTimers.get(id))
        refreshTimers.set(id, setTimeout(() => {
          refreshTimers.delete(id)
          const current = this.lists[id]
          if (current) void this.ensureRange(current.groupId, id, current.visibleFrom, current.visibleTo)
        }, REFRESH_DEBOUNCE_MS + Math.random() * REFRESH_JITTER_MS))
      }
    },

    resetForLogout() {
      for (const timer of refreshTimers.values()) clearTimeout(timer)
      refreshTimers.clear()
      versions.clear()
      inFlight.clear()
      this.lists = {}
    },
  },
})
